import cn from 'classnames';
import React, { useEffect, useRef } from 'react';

// REDUX
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import {
    getAccount,
    getCasinoMenu,
    getFixesState,
    getGlobalStyles,
    setCasinoMenu,
    setFixesState,
    setLeftHandMode,
    setWCAMode
} from '@Managers/Redux/AppState/AppState';
import { TCasinoMenu } from '@Managers/Redux/AppState/AppState.types';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';
import { userAction } from '@Managers/Redux/HttpState';
import LocalStorageManager from '@Managers/Storage/LocalStorageManager';
import { LocalStorageItemKeys } from '@Managers/Storage/LocalStorageManager.types';

import { EGameEvents, TUserAction } from '@Managers/Events/GameEvents';

// CSS
import styles from '@UILayer/UILayer.scss';

// VIEWS
import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import { FixStringParser } from '@Tools/FixStringParser';
import { MainFrame } from '@Views/index';

const ReadyView: React.FC<{}> = ({}) => {
    const dispatch = useAppDispatch();
    const globalStyles = useAppSelector(getGlobalStyles);
    const urlParams = ConfigLoader.urlParameters;
    const casinoData = useAppSelector(getCasinoMenu);
    const fixesState = useAppSelector(getFixesState);
    const gameStoreAccount = useAppSelector(getAccount);

    const fixesStateRef = useRef(fixesState);
    fixesStateRef.current = fixesState;

    const gameStoreAccountRef = useRef(gameStoreAccount);
    gameStoreAccountRef.current = gameStoreAccount;

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));

        document.title = `${ConfigLoader.getConfiguration(EConfigurationNames.GAME).gameTitle} - Promatic GAMES`;

        //SET SETTINGS BASED ON LOCAL STORAGE
        const localWcaMode = LocalStorageManager.readItem(LocalStorageItemKeys.WCA_MODE);
        if (localWcaMode) {
            dispatch(setWCAMode(localWcaMode === 'true' ? true : false));
        }

        const localLeftMode = LocalStorageManager.readItem(LocalStorageItemKeys.LEFT_HAND_MODE);
        if (localWcaMode) {
            dispatch(setLeftHandMode(localLeftMode === 'true' ? true : false));
        }

        // SET CASINO URL'S FROM URL ADDRESS
        const newCasinoMenu: TCasinoMenu = {};
        if (urlParams.cashierUrl && !casinoData.cashier) newCasinoMenu.cashier = { active: true, title: '', url: decodeURIComponent(urlParams.cashierUrl) };
        if (urlParams.homeUrl && !casinoData.lobby) newCasinoMenu.lobby = { active: true, title: '', url: decodeURIComponent(urlParams.homeUrl) };
        if (urlParams.backurl && !casinoData.lobby) newCasinoMenu.lobby = { active: true, title: '', url: decodeURIComponent(urlParams.backurl) };
        if (urlParams.historyUrl && !casinoData.history) newCasinoMenu.history = { active: true, title: '', url: decodeURIComponent(urlParams.historyUrl) };
        dispatch(setCasinoMenu({ ...casinoData, ...newCasinoMenu }));

        // GENERAL GAME ACTION REQUEST E.G SPIN, TAKE, GAMBLE
        EventsCenter.on(EGameEvents.SEND_ACTION_REQUEST, (data: TUserAction) => {
            let actionData = data;

            if (actionData.actionName === EActionName.SPIN) {
                const debugActive = ConfigLoader.getConfiguration(EConfigurationNames.UI_LAYER).debugTools?.active;
                const fixState = fixesStateRef.current;

                if (debugActive && fixesStateRef.current.active) {
                    let currentIndex = 0;
                    if (fixState.currentIndex !== undefined) {
                        currentIndex = fixState.currentIndex < fixState.fixCombinations.length - 1 ? fixState.currentIndex + 1 : 0;
                    }
                    const fix = FixStringParser(fixState.fixCombinations[currentIndex]);
                    actionData.fix = fix;
                    dispatch(setFixesState({ currentIndex: currentIndex }));
                }
            }

            if (gameStoreAccountRef.current.bonuses.freeSpins && !gameStoreAccountRef.current.bonuses.freeSpins.suspend) {
                actionData = { ...actionData, ...gameStoreAccountRef.current.bonuses.freeSpins.requestPayload };
            }

            dispatch(userAction(actionData));
        });
    }, []);

    const classes = cn(
        globalStyles.orientation,
        globalStyles.device,
        globalStyles.handMode,
        globalStyles.wcaMode,
        globalStyles.screenSize,
        styles.root_container
    );

    return (
        <>
            <div className={classes}>
                <MainFrame />
            </div>
        </>
    );
};

export default ReadyView;
